import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useFirestore } from "reactfire";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";

type YoucatMonthlyMaterial = {
  docId: string;
  monthKey: string;
  monthLabel: string;
  title: string;
  summary: string;
  coverImage: string;
  materialUrl: string;
  hasSeparateMaterial?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

type FormValues = {
  title: string;
  summary: string;
  materialUrl: string;
};

const EMPTY_FORM: FormValues = {
  title: "",
  summary: "",
  materialUrl: "",
};

export const YOUCAT_MONTHLY_MATERIALS_COLLECTION = "youcatMonthlyMaterials";

function getMonthLabel(monthKey: string) {
  const [year, month] = monthKey.split("-").map(Number);

  if (!year || !month) {
    return monthKey;
  }

  return new Date(year, month - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

export default function YoucatMonthlyMaterialsTab() {
  const firestoreInstance = useFirestore();
  const { showSnackbar } = useFeedback();
  const [materials, setMaterials] = useState<YoucatMonthlyMaterial[]>([]);
  const [monthKey, setMonthKey] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [formValues, setFormValues] = useState<FormValues>(EMPTY_FORM);
  const [selectedCoverImage, setSelectedCoverImage] = useState<File | null>(null);
  const [selectedMaterialFile, setSelectedMaterialFile] = useState<File | null>(
    null,
  );
  const [hasSeparateMaterial, setHasSeparateMaterial] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const editingMaterial = useMemo(
    () => materials.find((item) => item.monthKey === monthKey),
    [materials, monthKey],
  );

  async function loadMaterials() {
    try {
      setIsLoading(true);
      const snapshot = await getDocs(
        query(
          collection(firestoreInstance, YOUCAT_MONTHLY_MATERIALS_COLLECTION),
          orderBy("monthKey", "desc"),
        ),
      );
      setMaterials(
        snapshot.docs.map((materialDoc) => ({
          docId: materialDoc.id,
          ...(materialDoc.data() as Omit<YoucatMonthlyMaterial, "docId">),
        })),
      );
    } catch (error) {
      console.error("Failed to load YouCat monthly materials", error);
      showSnackbar("Failed to load YouCat monthly materials");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadMaterials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!editingMaterial) {
      setFormValues(EMPTY_FORM);
      setSelectedCoverImage(null);
      setSelectedMaterialFile(null);
      setHasSeparateMaterial(false);
      return;
    }

    setFormValues({
      title: editingMaterial.title || "",
      summary: editingMaterial.summary || "",
      materialUrl: editingMaterial.materialUrl || "",
    });
    setSelectedCoverImage(null);
    setSelectedMaterialFile(null);
    setHasSeparateMaterial(
      Boolean(editingMaterial.hasSeparateMaterial || editingMaterial.materialUrl),
    );
  }, [editingMaterial]);

  async function handleSave() {
    if (!monthKey) {
      showSnackbar("Please choose a month");
      return;
    }

    if (!formValues.title.trim()) {
      showSnackbar("Please enter a monthly material title");
      return;
    }

    try {
      setIsSaving(true);

      let coverImage = editingMaterial?.coverImage || "";
      let materialUrl = hasSeparateMaterial
        ? formValues.materialUrl.trim() || editingMaterial?.materialUrl || ""
        : "";

      if (selectedCoverImage) {
        const coverRef = ref(
          storage,
          `youcat/materials/${monthKey}/cover-${Date.now()}-${selectedCoverImage.name}`,
        );
        await uploadBytes(coverRef, selectedCoverImage);
        coverImage = await getDownloadURL(coverRef);
      }

      if (hasSeparateMaterial && selectedMaterialFile) {
        const fileRef = ref(
          storage,
          `youcat/materials/${monthKey}/file-${Date.now()}-${selectedMaterialFile.name}`,
        );
        await uploadBytes(fileRef, selectedMaterialFile);
        materialUrl = await getDownloadURL(fileRef);
      }

      if (hasSeparateMaterial && !materialUrl) {
        showSnackbar("Please provide a material file or material URL");
        setIsSaving(false);
        return;
      }

      await setDoc(
        doc(firestore, YOUCAT_MONTHLY_MATERIALS_COLLECTION, monthKey),
        {
          monthKey,
          monthLabel: getMonthLabel(monthKey),
          title: formValues.title.trim(),
          summary: formValues.summary.trim(),
          coverImage,
          materialUrl,
          hasSeparateMaterial,
          createdAt: editingMaterial?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      );

      showSnackbar("YouCat monthly material saved successfully");
      loadMaterials();
    } catch (error) {
      console.error("Failed to save YouCat monthly material", error);
      showSnackbar("Failed to save YouCat monthly material");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(docId: string) {
    if (!window.confirm("Delete this monthly material?")) {
      return;
    }

    try {
      await deleteDoc(doc(firestore, YOUCAT_MONTHLY_MATERIALS_COLLECTION, docId));
      showSnackbar("Monthly material deleted successfully");
      loadMaterials();
    } catch (error) {
      console.error("Failed to delete monthly material", error);
      showSnackbar("Failed to delete monthly material");
    }
  }

  return (
    <Box p={3}>
      <Typography variant="h6" fontWeight={600} mb={1}>
        Monthly YouCat Material
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Add the monthly cover image here. If there is a separate material file
        or link, enable it below; otherwise the public page will use the cover
        image alone with proper alignment.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="month"
            label="Month"
            value={monthKey}
            onChange={(event) => setMonthKey(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Material Title"
            value={formValues.title}
            onChange={(event) =>
              setFormValues((prev) => ({ ...prev, title: event.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Material Summary"
            multiline
            minRows={4}
            value={formValues.summary}
            onChange={(event) =>
              setFormValues((prev) => ({ ...prev, summary: event.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hasSeparateMaterial}
                onChange={(event) => setHasSeparateMaterial(event.target.checked)}
              />
            }
            label="There is a separate material to upload/open"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <FormLabel>Cover Image</FormLabel>
            <input
              type="file"
              accept="image/*"
              style={{ marginTop: 8 }}
              onChange={(event) =>
                setSelectedCoverImage(event.target.files?.[0] || null)
              }
            />
          </FormControl>
        </Grid>
        {hasSeparateMaterial ? (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Material URL"
                placeholder="Paste a PDF or external material link if needed"
                value={formValues.materialUrl}
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    materialUrl: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <FormLabel>Material File</FormLabel>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png,.webp"
                  style={{ marginTop: 8 }}
                  onChange={(event) =>
                    setSelectedMaterialFile(event.target.files?.[0] || null)
                  }
                />
              </FormControl>
            </Grid>
          </>
        ) : null}
        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            {isSaving
              ? "Saving..."
              : editingMaterial
                ? "Update Monthly Material"
                : "Save Monthly Material"}
          </Button>
        </Grid>
      </Grid>

      <Box mt={5}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          Existing Monthly Materials
        </Typography>
        {isLoading ? (
          <Typography>Loading monthly materials...</Typography>
        ) : !materials.length ? (
          <Typography color="text.secondary">
            No YouCat monthly materials added yet.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {materials.map((material) => (
              <Grid item xs={12} md={6} lg={4} key={material.docId}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="overline" color="warning.main" fontWeight={800}>
                      {material.monthLabel}
                    </Typography>
                    <Typography variant="h6" fontWeight={700} mb={1}>
                      {material.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {material.summary || "No summary added yet."}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={() => setMonthKey(material.monthKey)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleDelete(material.docId)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
}
